import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, StatTile, EmptyState, StatusPill } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2 } from "lucide-react";

export function BuyerDashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [ledger, setLedger] = useState([]);
  useEffect(() => {
    api.get("/buyer/leads").then((r) => setLeads(r.data));
    api.get("/buyer/ledger").then((r) => setLedger(r.data));
  }, []);
  const revenue = ledger.reduce((s, x) => s + (x.price || 0), 0);
  return (
    <>
      <PortalHeader eyebrow={`/ BUYER · ${user?.org_name || ""}`} title="Buyer control" testId="buyer-dashboard-header" />
      <PortalBody>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatTile label="Leads (routed)" value={leads.filter((l) => l.status === "routed").length} testId="buyer-stat-routed" />
          <StatTile label="New / pending" value={leads.filter((l) => l.status === "new").length} testId="buyer-stat-new" />
          <StatTile label="Ledger entries" value={ledger.length} testId="buyer-stat-ledger" />
          <StatTile label="Spend" value={`$${revenue.toFixed(2)}`} testId="buyer-stat-spend" />
        </div>
        <h2 className="font-heading text-xl font-bold mb-3">Recent leads</h2>
        {leads.length === 0 ? (
          <EmptyState testId="buyer-leads-empty">NO LEADS YET. SET UP A STANDING ORDER TO START RECEIVING.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>When</th><th>Type</th><th>Contact</th><th>State</th><th>Status</th></tr></thead>
              <tbody>
                {leads.slice(0, 25).map((l) => (
                  <tr key={l.id} data-testid={`buyer-lead-row-${l.id}`}>
                    <td className="font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="font-mono">{l.lead_type}</td>
                    <td className="text-xs">{l.contact?.email || l.contact?.name || "—"}</td>
                    <td className="text-xs font-mono">{(l.contact?.state || "").toUpperCase()}</td>
                    <td><StatusPill status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function BuyerStandingOrders() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ lead_type: "supporter", states: "PA,OH,MI", offices: "", daily_cap: 0, price_per_lead: 3.5, active: true });
  const [err, setErr] = useState("");
  const load = () => api.get("/buyer/standing-orders").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = {
        ...form,
        states: form.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        offices: form.offices.split(",").map((s) => s.trim()).filter(Boolean),
        daily_cap: parseInt(form.daily_cap, 10) || 0,
        price_per_lead: parseFloat(form.price_per_lead) || 0,
      };
      await api.post("/buyer/standing-orders", payload);
      setForm({ lead_type: "supporter", states: "", offices: "", daily_cap: 0, price_per_lead: 3.5, active: true });
      await load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };
  const toggle = async (row) => {
    await api.put(`/buyer/standing-orders/${row.id}`, { ...row, active: !row.active });
    await load();
  };
  return (
    <>
      <PortalHeader eyebrow="/ BUYER" title="Standing orders" testId="buyer-standing-header" />
      <PortalBody>
        <div className="dt6-card p-6 mb-6">
          <div className="dt6-chip mb-4">+ NEW STANDING ORDER</div>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="buyer-so-form">
            <div>
              <label className="dt6-label">Lead type</label>
              <select className="dt6-select" value={form.lead_type} onChange={(e)=>setForm({...form,lead_type:e.target.value})} data-testid="buyer-so-type">
                {["supporter","volunteer","donor","petition"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="dt6-label">States (comma-separated)</label>
              <input className="dt6-input" value={form.states} onChange={(e)=>setForm({...form,states:e.target.value})} placeholder="PA,OH" data-testid="buyer-so-states" />
            </div>
            <div>
              <label className="dt6-label">Offices</label>
              <input className="dt6-input" value={form.offices} onChange={(e)=>setForm({...form,offices:e.target.value})} placeholder="senate,house" data-testid="buyer-so-offices" />
            </div>
            <div>
              <label className="dt6-label">Daily cap (0 = unlimited)</label>
              <input className="dt6-input" type="number" min="0" value={form.daily_cap} onChange={(e)=>setForm({...form,daily_cap:e.target.value})} data-testid="buyer-so-cap" />
            </div>
            <div>
              <label className="dt6-label">Price per lead ($)</label>
              <input className="dt6-input" type="number" step="0.10" value={form.price_per_lead} onChange={(e)=>setForm({...form,price_per_lead:e.target.value})} data-testid="buyer-so-price" />
            </div>
            <div className="flex items-end">
              <button className="dt6-btn dt6-btn-primary w-full justify-center" data-testid="buyer-so-submit"><Plus size={14} /> Add order</button>
            </div>
            {err && <div className="md:col-span-3 text-sm text-[color:var(--dt6-danger)]">{err}</div>}
          </form>
        </div>

        {rows.length === 0 ? (
          <EmptyState testId="buyer-so-empty">NO STANDING ORDERS.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>Type</th><th>States</th><th>Cap/day</th><th>$/lead</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`buyer-so-row-${r.id}`}>
                    <td className="font-mono">{r.lead_type}</td>
                    <td className="text-xs">{(r.states || []).join(", ") || "any"}</td>
                    <td className="font-mono">{r.daily_cap || "∞"}</td>
                    <td className="font-mono">${r.price_per_lead}</td>
                    <td>{r.active ? <span className="dt6-chip dt6-chip-success">ACTIVE</span> : <span className="dt6-chip">PAUSED</span>}</td>
                    <td><button className="dt6-btn text-xs" onClick={() => toggle(r)} data-testid={`buyer-so-toggle-${r.id}`}>{r.active ? "Pause" : "Resume"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function BuyerLeads() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/buyer/leads").then((r) => setRows(r.data)); }, []);
  return (
    <>
      <PortalHeader eyebrow="/ BUYER" title="My leads" testId="buyer-leads-header" />
      <PortalBody>
        {rows.length === 0 ? (
          <EmptyState testId="buyer-leads-empty">NO LEADS YET.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>When</th><th>Type</th><th>Contact</th><th>UTM</th><th>Consent</th><th>Status</th></tr></thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} data-testid={`buyer-leads-row-${l.id}`}>
                    <td className="font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="font-mono">{l.lead_type}</td>
                    <td className="text-xs">{l.contact?.email || l.contact?.name || "—"}</td>
                    <td className="text-xs text-[color:var(--dt6-muted)] font-mono">{Object.keys(l.utm || {}).length ? JSON.stringify(l.utm) : "—"}</td>
                    <td>{l.consent?.given ? <span className="dt6-chip dt6-chip-success">YES</span> : <span className="dt6-chip dt6-chip-danger">NO</span>}</td>
                    <td><StatusPill status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function BuyerLedger() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/buyer/ledger").then((r) => setRows(r.data)); }, []);
  const total = rows.reduce((s, r) => s + (r.price || 0), 0);
  return (
    <>
      <PortalHeader eyebrow="/ BUYER" title="Ledger" testId="buyer-ledger-header" />
      <PortalBody>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatTile label="Entries" value={rows.length} testId="buyer-ledger-count" />
          <StatTile label="Spend" value={`$${total.toFixed(2)}`} testId="buyer-ledger-total" />
          <StatTile label="Avg. cost / lead" value={rows.length ? `$${(total / rows.length).toFixed(2)}` : "—"} testId="buyer-ledger-avg" />
        </div>
        {rows.length === 0 ? (
          <EmptyState testId="buyer-ledger-empty">LEDGER IS EMPTY.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>When</th><th>Lead</th><th>Price</th><th>Status</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`buyer-ledger-row-${r.id}`}>
                    <td className="font-mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="font-mono text-xs">{String(r.lead_id).slice(0, 8).toUpperCase()}</td>
                    <td className="font-mono">${(r.price || 0).toFixed(2)}</td>
                    <td><span className="dt6-chip dt6-chip-amber">{r.status?.toUpperCase()}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function BuyerProfile() {
  const [form, setForm] = useState({ display_name: "", contact_email: "", contact_phone: "", notes: "" });
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/buyer/profile").then((r) => setForm((prev) => ({ ...prev, ...r.data })));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setOk(false); setErr("");
    try { await api.put("/buyer/profile", form); setOk(true); }
    catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <>
      <PortalHeader eyebrow="/ BUYER" title="Profile" testId="buyer-profile-header" />
      <PortalBody>
        <form onSubmit={submit} className="dt6-card p-6 max-w-xl" data-testid="buyer-profile-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className="dt6-label">Display name</label><input className="dt6-input" required value={form.display_name || ""} onChange={(e)=>setForm({...form,display_name:e.target.value})} data-testid="buyer-profile-name" /></div>
            <div><label className="dt6-label">Contact email</label><input className="dt6-input" type="email" required value={form.contact_email || ""} onChange={(e)=>setForm({...form,contact_email:e.target.value})} data-testid="buyer-profile-email" /></div>
            <div><label className="dt6-label">Phone</label><input className="dt6-input" value={form.contact_phone || ""} onChange={(e)=>setForm({...form,contact_phone:e.target.value})} data-testid="buyer-profile-phone" /></div>
            <div className="md:col-span-2"><label className="dt6-label">Notes</label><textarea className="dt6-textarea" rows={3} value={form.notes || ""} onChange={(e)=>setForm({...form,notes:e.target.value})} data-testid="buyer-profile-notes" /></div>
          </div>
          {ok && <div className="mt-4 text-sm text-[color:var(--dt6-success)]" data-testid="buyer-profile-ok">SAVED.</div>}
          {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]" data-testid="buyer-profile-error">{err}</div>}
          <div className="mt-6 flex justify-end"><button className="dt6-btn dt6-btn-primary" data-testid="buyer-profile-submit">Save</button></div>
        </form>
      </PortalBody>
    </>
  );
}
